import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Alert, Box, Button, useMediaQuery, useTheme } from '@mui/material';
import Dialog from 'components/dialog';
import Select from 'components/select';
import Switch from 'components/switch';
import FlexBox from 'components/flex-box';
import TextField from 'components/text-field';
import NumberField from 'components/number-field';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { getCategories } from '../api/get-categories';
import { postTreatment } from '../api/post-treatment';

import { Treatment } from '../types/treatment.type';
import { TreatmentType } from '../types/treatment-type.enum';
import { CATEGORY_KEYS } from '../types/treatment-category-keys';
import { CreateTreatmentInput } from '../types/create-treatment-input.type';

type Props = {
  treatmentCodes: string[];
};

const PAGE_NAME_ID = 'selfService.treatmentCatalog';

const CreateTreatment: React.FC<Props> = ({ treatmentCodes }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // #region STATE
  const [hiddenTreatment] = useState<boolean>(false);
  const [duplicatedCode, setDuplicatedCode] = useState(false);
  // #endregion STATE

  // #region FORMS
  const { handleSubmit, control, getValues, setError, clearErrors } = useForm<CreateTreatmentInput>({
    defaultValues: {
      code: '',
      type: '',
      brand: '',
      name: '',
      hidden: false,
      treatmentCategory: '',
    },
    shouldUnregister: true,
  });
  // #endregion FORMS

  // #region GET DATA
  const { data: categories } = useGet(['getCategories'], () => getCategories(String(account.company!.companyId)), {
    enabled: !!account.company!.companyId,
  });
  // #endregion GET DATA

  // #region POST DATA
  const createTreatment = usePost<Treatment, { request: CreateTreatmentInput }>(
    (request) => postTreatment(account.company!.companyId, request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getTreatments']
      });
      dialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  // #endregion POST DATA

  // #region HANDLERS
  const handleCreateTreatment = (request: CreateTreatmentInput) => {
    createTreatment.mutate({
      request: {
        ...request,
        price: Number(request.price),
      },
    });
  };

  const onKeyDown = (e: any) => e.stopPropagation();

  const handleCodeError = (isDuplicate: boolean) => {
    setDuplicatedCode(isDuplicate);
    isDuplicate ? setError('code', {}) : clearErrors('code');
  };

  // #endregion HANDLERS

  return (
    <Box width={{ xs: '100%', md: 'auto' }} flexDirection={'column'} display={'flex'} gap={3} mb={3}>
      <Dialog
        maxWidth="sm"
        ref={dialogRef}
        sx={{ zIndex: 1510 }}
        onClose={() => handleCodeError(false)}
        onKeyDown={(e) => e.key === 'Tab' && e.stopPropagation()}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.createTreatmentDialog.dialog.title` })}
        button={<Button fullWidth>{intl.formatMessage({ id: `${PAGE_NAME_ID}.createTreatmentDialog.dialog.title` })}</Button>}>
        <form onSubmit={handleSubmit(handleCreateTreatment)}>
          {createTreatment.isPending && <LoadingSpinner overlay />}
          <FlexBox>
            {duplicatedCode && (
              <Alert severity="warning">
                <FormattedMessage id={intl.formatMessage({ id: `${PAGE_NAME_ID}.createTreatmentDialog.dialog.duplicatedCode` })} />
              </Alert>
            )}
            <TextField
              dark
              fullWidth
              stopPropagation
              onKeyDown={onKeyDown}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createTreatmentDialog.dialog.name` })}
              controller={{
                name: 'name',
                control: control,
                rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
              }}
            />
            <TextField
              dark
              fullWidth
              stopPropagation
              onKeyDown={onKeyDown}
              maxLength={50}
              onBlur={() => {
                const codeValue = getValues('code').trim();
                handleCodeError(codeValue !== '' ? treatmentCodes.includes(codeValue) : false);
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createTreatmentDialog.dialog.code` })}
              controller={{
                name: 'code',
                control: control,
              }}
            />
            <TextField
              dark
              fullWidth
              stopPropagation
              onKeyDown={onKeyDown}
              maxLength={50}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createTreatmentDialog.dialog.brand` })}
              controller={{
                name: 'brand',
                control: control,
                rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
              }}
            />
            <Box display={'flex'} gap={4} flexWrap={isMobile ? 'wrap' : 'nowrap'}>
              <Select
                dark
                nested
                fullWidth
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createTreatmentDialog.dialog.type` })}
                controller={{
                  name: 'type',
                  control: control,
                  rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                }}>
                {Object.values(TreatmentType).map((value, index) => (
                  <RadioSelectItem key={index} value={value}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.types.${value}`} />
                  </RadioSelectItem>
                ))}
              </Select>

              <Select
                dark
                nested
                fullWidth
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createTreatmentDialog.dialog.treatmentCategory` })}
                controller={{
                  name: 'treatmentCategory',
                  control: control,
                  rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                }}>
                {categories?.map((category) => {
                  const translationKey = CATEGORY_KEYS[category.name];
                  return (
                    <RadioSelectItem key={category.id} value={category.id}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.types.${translationKey}`} defaultMessage={category.name} />
                    </RadioSelectItem>
                  );
                })}
              </Select>
            </Box>

            <NumberField
              dark
              fullWidth
              onKeyDown={onKeyDown}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createTreatmentDialog.dialog.price` })}
              controller={{
                name: 'price',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                  },
                },
              }}
            />

            <Switch
              dark
              value={hiddenTreatment}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createTreatmentDialog.dialog.inactive` })}
              controller={{
                name: 'hidden',
                control: control,
              }}
            />
            <Box display={'flex'} justifyContent={'end'}>
              <Button type="submit" disabled={duplicatedCode}>
                <FormattedMessage id={`general.save`} />
              </Button>
            </Box>
          </FlexBox>
        </form>
      </Dialog>
    </Box>
  );
};

export default React.memo(CreateTreatment);
